import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle2, XCircle, Loader2, Chrome } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

/**
 * Extension Authentication Page
 * 
 * This page handles authentication for the Wedemo browser extension.
 * It communicates with the extension via postMessage to share auth tokens.
 */
export default function ExtensionAuth() {
  const { session, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'checking' | 'authenticated' | 'not-authenticated' | 'sent' | 'error'>('checking');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAuth = async () => {
      console.log('[ExtensionAuth] Checking authentication status...');
      
      // Wait for auth context to load
      if (authLoading) {
        return;
      }

      if (!session) {
        console.log('[ExtensionAuth] No session found, redirecting to auth');
        setStatus('not-authenticated');
        return;
      }

      console.log('[ExtensionAuth] Session found, sending to extension');
      setStatus('authenticated');
      
      try {
        // Prepare auth data for extension
        const authData = {
          access_token: session.access_token,
          refresh_token: session.refresh_token,
          expires_at: session.expires_at,
          user: {
            id: session.user.id,
            email: session.user.email,
            user_metadata: session.user.user_metadata
          }
        };

        // Send auth data to extension
        // First, try sending directly to the extension that opened this tab
        // This works because the tab was opened by the extension
        try {
          // Send message back to the extension that opened this tab
          if (window.opener || window.chrome?.runtime?.sendMessage) {
            console.log('[ExtensionAuth] Attempting to send auth to extension');
            
            // Try to send directly to the extension
            chrome?.runtime?.sendMessage(
              chrome?.runtime?.id || 'pnfcogggacflhkffanpdkjakjleongbh', // Use extension ID if available
              {
                type: 'WEDEMO_AUTH_SUCCESS',
                session: authData,
                timestamp: Date.now()
              },
              (response) => {
                if (chrome?.runtime?.lastError) {
                  console.warn('[ExtensionAuth] Direct message failed:', chrome.runtime.lastError);
                  // Continue with other methods
                } else {
                  console.log('[ExtensionAuth] Extension responded:', response);
                  setStatus('sent');
                  toast({
                    title: 'Authentication Successful',
                    description: 'You can now close this window and return to the extension.',
                  });
                  // Auto-close after 3 seconds
                  setTimeout(() => {
                    window.close();
                  }, 3000);
                  return;
                }
              }
            );
          }
        } catch (e) {
          console.warn('[ExtensionAuth] Direct messaging not available:', e);
        }
        
        // Fallback to other methods
        const extensionId = new URLSearchParams(window.location.search).get('extensionId');
        
        if (extensionId) {
          // If extension ID is provided, send directly to that extension
          console.log('[ExtensionAuth] Sending auth to specific extension:', extensionId);
          window.chrome?.runtime?.sendMessage(
            extensionId,
            {
              type: 'WEDEMO_AUTH_SUCCESS',
              session: authData,
              timestamp: Date.now()
            },
            (response) => {
              if (chrome.runtime.lastError) {
                console.error('[ExtensionAuth] Error sending message:', chrome.runtime.lastError);
                setError('Failed to communicate with extension');
                setStatus('error');
              } else {
                console.log('[ExtensionAuth] Extension responded:', response);
                setStatus('sent');
                toast({
                  title: 'Authentication Successful',
                  description: 'You can now close this window and return to the extension.',
                });
                // Auto-close after 3 seconds
                setTimeout(() => {
                  window.close();
                }, 3000);
              }
            }
          );
        } else {
          // Broadcast to any listening extension
          console.log('[ExtensionAuth] Broadcasting auth to all extensions');
          
          // Try multiple communication methods for compatibility
          
          // Method 1: Use BroadcastChannel API (same-origin)
          try {
            const channel = new BroadcastChannel('wedemo_extension_auth');
            channel.postMessage({
              type: 'WEDEMO_AUTH_SUCCESS',
              session: authData,
              timestamp: Date.now()
            });
            channel.close();
            console.log('[ExtensionAuth] Sent via BroadcastChannel');
          } catch (e) {
            console.warn('[ExtensionAuth] BroadcastChannel not available:', e);
          }

          // Method 2: Use window.postMessage for iframe communication
          window.postMessage(
            {
              type: 'WEDEMO_AUTH_SUCCESS',
              session: authData,
              timestamp: Date.now()
            },
            window.location.origin
          );
          console.log('[ExtensionAuth] Sent via postMessage');

          // Method 3: Store in localStorage for extension to read
          localStorage.setItem('wedemo_extension_auth', JSON.stringify({
            type: 'WEDEMO_AUTH_SUCCESS',
            session: authData,
            timestamp: Date.now()
          }));
          console.log('[ExtensionAuth] Stored in localStorage');

          setStatus('sent');
          toast({
            title: 'Authentication Successful',
            description: 'You can now close this window and return to the extension.',
          });
          
          // Auto-close after 3 seconds
          setTimeout(() => {
            window.close();
          }, 3000);
        }
      } catch (err) {
        console.error('[ExtensionAuth] Error sending auth data:', err);
        setError(err instanceof Error ? err.message : 'Failed to send authentication data');
        setStatus('error');
      }
    };

    handleAuth();
  }, [session, authLoading]);

  const handleSignIn = () => {
    // Redirect to auth page with return URL
    navigate('/auth?returnTo=' + encodeURIComponent('/extension-auth'));
  };

  const handleRetry = () => {
    setStatus('checking');
    setError(null);
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <Chrome className="h-8 w-8 text-primary" />
            <CardTitle className="text-2xl">Extension Authentication</CardTitle>
          </div>
          <CardDescription>
            Authenticate your Wedemo browser extension to enable cloud sync
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'checking' && (
            <div className="flex items-center gap-3 py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="text-muted-foreground">Checking authentication status...</span>
            </div>
          )}

          {status === 'not-authenticated' && (
            <div className="space-y-4">
              <Alert>
                <AlertTitle>Sign in required</AlertTitle>
                <AlertDescription>
                  You need to sign in to your Wedemo account to use the extension with cloud sync.
                </AlertDescription>
              </Alert>
              <Button onClick={handleSignIn} className="w-full">
                Sign In to Wedemo
              </Button>
            </div>
          )}

          {status === 'authenticated' && (
            <div className="flex items-center gap-3 py-4">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-muted-foreground">Sending authentication to extension...</span>
            </div>
          )}

          {status === 'sent' && (
            <div className="space-y-4">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-900">Success!</AlertTitle>
                <AlertDescription className="text-green-800">
                  Authentication sent to your browser extension. 
                  You can close this window and return to the extension.
                </AlertDescription>
              </Alert>
              <div className="text-sm text-muted-foreground text-center">
                This window will close automatically in a few seconds...
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertTitle>Authentication Error</AlertTitle>
                <AlertDescription>
                  {error || 'An error occurred while authenticating the extension.'}
                </AlertDescription>
              </Alert>
              <Button onClick={handleRetry} variant="outline" className="w-full">
                Try Again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}